// react
import {
	MouseEvent,
	SyntheticEvent,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

// types
import {
	Account,
	AccountPayload,
	Act,
	ActionButton,
	Activity,
	Contract,
	Document,
	Entity,
	EntityPayload,
	CustomFieldDefinition,
	Loan,
	Pagination,
	Permissions,
	IfrPlan,
	IfrPlanVersion,
	Project,
	ProjectPayload,
	Transaction,
	User,
	UserProject,
	IfrPlanVersionPayload,
	Ifr,
	IfrPayload,
	IfrTables,
	UserRoleWbg,
} from '../../../@types';
import { entityTypesReadOnly, userRolesWbg } from '../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import {
	ContextAccountsDesignated,
	ContextAccountsLocal,
	ContextActivities,
	ContextAdvances,
	ContextBeneficiaries,
	ContextCashBenefits,
	ContextContracts,
	ContextNonCashBenefits,
	ContextDocumentsBeneficiaries,
	ContextDocumentsSuppliers,
	ContextEntities,
	ContextEntitiesSummary,
	ContextHistoricalTransactions,
	ContextIfr,
	ContextIfrPlan,
	ContextIfrPlans,
	ContextIfrs,
	ContextLoans,
	ContextOpEx,
	ContextPayments,
	ContextProject,
	ContextPaymentRefunds,
	ContextReimbursements,
	ContextSuppliers,
	ContextTransfers,
	ContextUsersPermissions,
	ContextActs,
	ContextUserProjectPermissions,
	ContextIfrPlanVersion,
	ContextSpecialCommitments,
} from './Project.context';
import { ContextUser, ContextUserEntity } from '../../../App.context';

// router
import {
	Link as RouterLink,
	Outlet,
	useLocation,
	useParams,
	useSearchParams,
} from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';
import { useIfr } from '../../../hooks/useIfr';

// mui
import { Box, Grid, TooltipProps } from '@mui/material';

// layouts
import { Authenticated } from '../../layouts/Authenticated/Authenticated';

// ui
import { Breadcrumbs, BreadcrumbsProps } from '../../ui/Breadcrumbs/Breadcrumbs';
import { BtnFab } from '../../ui/BtnFab/BtnFab';
import { Empty } from '../../ui/Empty/Empty';
import { Loading } from '../../ui/Loading/Loading';
import { Tabs, TabProps } from '../../ui/Tabs/Tabs';
import { Title } from '../../ui/Title/Title';

// dialogs
import { DialogAddIncident } from '../../dialogs/DialogAddIncident/DialogAddIncident';
import { DialogOnboardProject } from '../../dialogs/DialogOnboardProject/DialogOnboardProject';

// project sidebar layout
import { ProjectSidebar } from '../../layouts/ProjectSidebar/ProjectSidebar';

// icons
import {
	InsertDriveFileOutlined as IconInsertDriveFile,
	LocalAtmOutlined as IconLocalAtm,
	PaidOutlined as IconPaid,
	QuestionMarkOutlined as IconQuestionMark,
	SettingsOutlined as IconSettings,
	SwapHorizOutlined as IconTransfer,
	VolunteerActivismOutlined as IconVolunteerActivism,
} from '@mui/icons-material';

// utils
import { paramPopulateActivities } from '../../../utils/activities';
import { defaultPagination } from '../../../utils/defaults';
import { permissionsAdmin, permissionsFnFa, permissionsReadOnly } from '../../../utils/permissions';
import { buildV2ProjectUrl, buildV1ProjectUrl } from '../../../utils/v2Host';

// config
import { featureFlags } from '../../../config/featureFlags';

const hydrateProjectCustomFields = (
	project: Project,
	definitions: CustomFieldDefinition[]
): Project => {
	return {
		...project,
		customFieldDefinitions: definitions,
	};
};

export const PageProject = () => {
	// hooks
	const { patchIfr, patchIfrPlanVersion } = useIfr();
	const { pathname } = useLocation();
	const { id, idIfr, planId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);
	const { setUserEntity, userEntity } = useContext(ContextUserEntity);

	// defaults
	const getTabByLocation = useCallback((pathname) => {
		// Check spend-plans FIRST since its sub-routes contain patterns like 'activities', 'contracts'
		if (pathname.includes('spend-plans')) {
			return 'spend-plans';
		} else if (pathname.includes('invoices-for-processing')) {
			return 'invoices-for-processing';
		} else if (pathname.includes('accounts')) {
			return 'accounts';
		} else if (pathname.includes('activities')) {
			return 'overview';
		} else if (pathname.includes('contracts')) {
			return 'contracts';
		} else if (pathname.includes('entities')) {
			return 'entities';
		} else if (pathname.includes('financing')) {
			return 'financing';
		} else if (pathname.includes('transactions')) {
			return 'transactions';
		} else if (pathname.includes('settings')) {
			return 'settings';
		} else if (pathname.includes('suppliers')) {
			return 'suppliers';
		} else if (pathname.includes('beneficiaries')) {
			return 'beneficiaries';
		} else if (
			pathname.includes('ifr') ||
			pathname.includes('plan') ||
			pathname.includes('reports')
		) {
			return 'reports';
		} else {
			return 'overview';
		}
	}, []);

	const defaultTabValue = useMemo(() => {
		return getTabByLocation(pathname);
	}, [getTabByLocation, pathname]);

	// params
	const paramBeneficiary = searchParams.get('beneficiary');
	const paramCategory = searchParams.get('category');
	const paramComponent = searchParams.get('component');
	const paramContract = searchParams.get('contract');
	const paramDialog = searchParams.get('dialog');
	const paramEntities = searchParams.get('entities');
	const paramEntity = searchParams.get('entity');
	const paramExpenseType = searchParams.get('expenseType');
	const paramFinancier = searchParams.get('financier');
	const paramFromAccount = searchParams.get('fromAccount');
	const paramFromEntity = searchParams.get('fromEntity');
	const paramHasTransactions = searchParams.get('hasTransactions');
	const paramIsHeadless = searchParams.get('isHeadless');
	const paramIsMock = searchParams.get('isMock');
	const paramIsOnChain = searchParams.get('isOnChain');
	const paramIsWbg = searchParams.get('isWbg');
	const paramLoan = searchParams.get('loan');
	const paramOrder = searchParams.get('order') || 'DESC';
	const paramOrderBy = searchParams.get('orderBy') || 'created';
	const paramPage = Number(searchParams.get('page') || 1);
	const paramQ = searchParams.get('q');
	const paramStatus = searchParams.get('status');
	const paramSubtype = searchParams.get('subtype');
	const paramSupplier = searchParams.get('supplier');
	const paramToAccount = searchParams.get('toAccount');
	const paramToEntity = searchParams.get('toEntity');
	const paramTags = searchParams.get('tags');
	const paramType = searchParams.get('type');
	const paramActor = searchParams.get('actor');
	const defaultPaginationWithPageParam = {
		...defaultPagination,
		page: paramPage,
	};

	// state
	const [accountsDesignated, setAccountsDesignated] = useState<Account[] | AccountPayload[]>([]);
	const [accountsLocal, setAccountsLocal] = useState<Account[] | AccountPayload[]>([]);
	const [acts, setActs] = useState<Act[]>([]);
	const [activities, setActivities] = useState<Activity[]>([]);
	const [advances, setAdvances] = useState<Transaction[]>([]);
	const [beneficiaries, setBeneficiaries] = useState<Entity[]>([]);
	const [cashBenefits, setCashBenefits] = useState<Transaction[]>([]);
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [nonCashBenefits, setNonCashBenefits] = useState<Transaction[]>([]);
	const [documentsBeneficiaries, setDocumentsBeneficiaries] = useState<Document[]>([]);
	const [documentsSuppliers, setDocumentsSuppliers] = useState<Document[]>([]);
	const [entities, setEntities] = useState<Entity[] | EntityPayload[]>([]);
	const [entitiesSummary, setEntitiesSummary] = useState<Entity[]>([]);
	const [historicalTransactions, setHistoricalTransactions] = useState<Document[]>([]);
	const [ifr, setIfr] = useState<Ifr | null>(null);
	const [ifrPayload, setIfrPayload] = useState<IfrPayload | null>(null);
	const [ifrPlan, setIfrPlan] = useState<IfrPlan | null>(null);
	const [ifrPlans, setIfrPlans] = useState<IfrPlan[]>([]);
	const [ifrPlanVersion, setIfrPlanVersion] = useState<IfrPlanVersion | null>(null);
	const [ifrPlanVersionPayload, setIfrPlanVersionPayload] = useState<IfrPlanVersionPayload | null>(
		null
	);
	const [ifrs, setIfrs] = useState<Ifr[]>([]);
	const [ifrTables, setIfrTables] = useState<IfrTables>({});
	const [loans, setLoans] = useState<Loan[]>([]);
	const [opEx, setOpEx] = useState<Transaction[]>([]);
	const [paginationAccountsDesignated, setPaginationAccountsDesignated] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationAccountsLocal, setPaginationAccountsLocal] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationActivities, setPaginationActivities] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationActs, setPaginationActs] = useState<Pagination>(defaultPaginationWithPageParam);
	const [paginationAdvances, setPaginationAdvances] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationBeneficiaries, setPaginationBeneficiaries] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationCashBenefits, setPaginationCashBenefits] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationContracts, setPaginationContracts] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationNonCashBenefits, setPaginationNonCashBenefits] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationDocumentsBeneficiaries, setPaginationDocumentsBeneficiaries] =
		useState<Pagination>(defaultPaginationWithPageParam);
	const [paginationDocumentsSuppliers, setPaginationDocumentsSuppliers] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationEntities, setPaginationEntities] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationHistoricalTransactions, setPaginationHistoricalTransactions] =
		useState<Pagination>(defaultPaginationWithPageParam);
	const [paginationLoans, setPaginationLoans] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationOpEx, setPaginationOpEx] = useState<Pagination>(defaultPaginationWithPageParam);
	const [paginationPayments, setPaginationPayments] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationPaymentRefunds, setPaginationPaymentRefunds] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationIfrPlans, setPaginationIfrPlans] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationIfrs, setPaginationIfrs] = useState<Pagination>(defaultPaginationWithPageParam);
	const [paginationReimbursements, setPaginationReimbursements] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationSpecialCommitments, setPaginationSpecialCommitments] = useState<Pagination>(
		defaultPaginationWithPageParam
	);

	const [paginationSuppliers, setPaginationSuppliers] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationTransfers, setPaginationTransfers] = useState<Pagination>(
		defaultPaginationWithPageParam
	);
	const [paginationUsersPermissions, setPaginationUsersPermissions] = useState<Pagination>({
		...defaultPaginationWithPageParam,
		perPage: 5,
	});
	const [payments, setPayments] = useState<Transaction[]>([]);
	const [project, setProject] = useState<Project | null>(null);
	const [paymentRefunds, setPaymentRefunds] = useState<Transaction[]>([]);
	const [reimbursements, setReimbursements] = useState<Transaction[]>([]);
	const [specialCommitments, setSpecialCommitments] = useState<Transaction[]>([]);
	const [suppliers, setSuppliers] = useState<Entity[]>([]);
	const [tabValue, setTabValue] = useState(defaultTabValue);
	const [transfers, setTransfers] = useState<Transaction[]>([]);
	const [usersPermissions, setUsersPermissions] = useState<User[]>([]);

	// V2 redirect: if a V2 project is opened on a recognized V1 deployment
	// (dev/qa/prod), send the user to the V2 site for the same project.
	// Covers direct navigation / bookmarks / refresh; LinkProject covers clicks.
	useEffect(() => {
		if (project?.version !== '2' || !project.idWbg) {
			return;
		}

		const v2Url = buildV2ProjectUrl(project.idWbg);

		if (v2Url) {
			window.location.replace(v2Url);
		}
	}, [project?.version, project?.idWbg]);

	// V1 redirect: if a V1 project is opened on a recognized V2 deployment
	// (dev/qa/prod), send the user back to the V1 site for the same project.
	// V1 projects are not operated within the 2.0 platform — except for the
	// V2 onboarding page (settings/v2), which is the entry point used to
	// upgrade a V1 project into the 2.0 platform and must remain reachable.
	useEffect(() => {
		if (
			project?.version === '2' ||
			!project?.idWbg ||
			pathname.includes('settings/v2')
		) {
			return;
		}

		const v1Url = buildV1ProjectUrl(project.idWbg);

		if (v1Url) {
			window.location.replace(v1Url);
		}
	}, [project?.version, project?.idWbg, pathname]);

	// vars
	const hasOnlyIfrTrials = user && userRolesWbg.includes(user.role as UserRoleWbg);
	const isHeadless = paramIsHeadless === 'true';
	const isMock = paramIsMock === 'true';
	const isWbg = paramIsWbg === 'true';
	const isOnboarded = Boolean(project && project.id) && !isWbg;
	const isStaff = user && user.role === 'STAFF';
	const loansEnabled = loans.filter((l) => l.status === 'ENABLED');
	const hasLoansEnabled = loansEnabled.length > 0;

	// define user project permissions
	const userProjectPermissions = useMemo(() => {
		let permissions: Permissions = {};

		if (user?.role === 'FNFA') {
			permissions = permissionsFnFa;
		} else if (user?.role === 'STAFF') {
			permissions = permissionsReadOnly;
		} else if (user?.role === 'WFA') {
			permissions = permissionsAdmin;
		} else {
			const userProject = (user?.projects as UserProject[])?.find((p) => p?.id === project?.id);

			if (userProject) {
				permissions = userProject.permissions;
			}
		}

		return permissions;
	}, [project?.id, user]);

	const hasTransactionsReadPermissions =
		userProjectPermissions?.cashBenefit?.read ||
		userProjectPermissions?.nonCashBenefit?.read ||
		userProjectPermissions?.opEx?.read ||
		userProjectPermissions?.payment?.read ||
		userProjectPermissions?.replenishment?.read ||
		userProjectPermissions?.transfer?.read;

	// Check if user has verification section assignments (for "Invoices for Processing" tab)
	const [hasSectionAssignments, setHasSectionAssignments] = useState(false);

	useEffect(() => {
		if (!isOnboarded || project?.version !== '2' || !user?.id || !accessToken || !idToken) return;

		const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/v1\/?$/, '');
		const url = `${apiUrl}/v2/invoices?project=${project.id}&assignedTo=${user.id}&limit=1`;

		fetch(url, {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				'Content-Type': 'application/json',
				User: String(idToken),
			},
		})
			.then((r) => r.json())
			.then((res) => {
				if (res.data && (Array.isArray(res.data) ? res.data.length > 0 : !!res.data)) {
					setHasSectionAssignments(true);
				}
			})
			.catch(() => {
				/* ignore — tab simply won't appear */
			});
	}, [isOnboarded, project?.version, project?.id, user?.id, accessToken, idToken]);

	let urlWbg = `${process.env.REACT_APP_API_URL}/wbg?q=${id}`;

	if (isMock) {
		urlWbg += `&resource=mock`;
	}

	const fetchOptions = useMemo(
		() => ({
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		}),
		[accessToken, idToken]
	);

	// fetch wbg project
	const { isLoading: isLoadingWbgProject } = useFetch({
		isEnabled: isWbg,
		url: urlWbg,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				const project = res.data.find((proj: Project) => proj.idWbg === id);

				if (project) {
					setProject(project);
					setLoans(project.loans);
					setAccountsDesignated(project.accounts);
					setEntities(project.entities);
				}
			}
		},
	});

	// fetch project
	const { isLoading: isLoadingProject, refetch: refetchProject } = useFetch({
		isEnabled: !isWbg,
		url: `${process.env.REACT_APP_API_URL}/projects?idWbg=${id}&fields=country,currencies,dateConfirmationReceipt,fiscalYearEnd,fiscalYearStart,id,idWbg,locale,modified,name,region,status,version&populate=activities,activities.actor,country,region,practice,entities,users`,
		options: fetchOptions,
		onSuccess: async (res) => {
			if (res.data) {
				const baseProject = res.data[0] as Project;

				// Set the base project immediately so the page renders as soon as
				// this fetch resolves. `useFetch` flips `isLoading` to false without
				// awaiting this async callback, so deferring `setProject` until after
				// the custom-fields request below would briefly leave the page with
				// `isLoading === false` and `project === null`, flashing the
				// "No Project Found" empty state before the data lands.
				setProject(baseProject);

				try {
					const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/v1\/?$/, '');
					const response = await fetch(
						`${apiUrl}/v2/custom-field-definitions?project=${baseProject.id}&limit=100`,
						{
							headers: {
								Authorization: `Bearer ${accessToken}`,
								'Content-Type': 'application/json',
								User: String(idToken),
							},
						}
					);

					const customFieldsResponse = await response.json();
					if (Array.isArray(customFieldsResponse?.data)) {
						const definitions = customFieldsResponse.data as CustomFieldDefinition[];

						setProject((projectOld) =>
							hydrateProjectCustomFields(projectOld ?? baseProject, definitions)
						);
					}
				} catch {
					// Keep project payload usable even if the custom definitions endpoint is unavailable.
				}
			}
		},
	});

	// fetch designated accounts
	let fetchAccountsDesignatedUrl = `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&type=DESIGNATED&populate=accounts,activities,activities.actor,country,entities,loans&orderBy=${paramOrderBy}&order=${paramOrder}&page=${paramPage}&limit=${paginationAccountsDesignated.perPage}`;

	fetchAccountsDesignatedUrl += `&status=${paramStatus || 'ACTIVE'}`;

	if (paramIsOnChain) {
		fetchAccountsDesignatedUrl += `&isOnChain=${paramIsOnChain}`;
	}

	if (paramQ) {
		fetchAccountsDesignatedUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchAccountsDesignated,
		isLoading: isLoadingAccountsDesignated,
		refetch: refetchAccountsDesignated,
	} = useFetch({
		isLazy: true,
		url: fetchAccountsDesignatedUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setAccountsDesignated(res.data);
			}

			if (res.pagination) {
				setPaginationAccountsDesignated(res.pagination);
			}
		},
	});

	// fetch local accounts
	let fetchAccountsLocalUrl = `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&type=LOCAL&populate=accounts,activities,activities.actor,country,entities,loans&orderBy=${paramOrderBy}&order=${paramOrder}&page=${paramPage}&limit=${paginationAccountsLocal.perPage}`;

	fetchAccountsLocalUrl += `&status=${paramStatus || 'ACTIVE'}`;

	if (paramIsOnChain) {
		fetchAccountsLocalUrl += `&isOnChain=${paramIsOnChain}`;
	}

	if (paramQ) {
		fetchAccountsLocalUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchAccountsLocal,
		isLoading: isLoadingAccountsLocal,
		refetch: refetchAccountsLocal,
	} = useFetch({
		isLazy: true,
		url: fetchAccountsLocalUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setAccountsLocal(res.data);
			}

			if (res.pagination) {
				setPaginationAccountsLocal(res.pagination);
			}
		},
	});

	// fetch loans
	let fetchLoansUrl = `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&populate=entities,accounts`;

	if (paramFinancier) {
		fetchLoansUrl += `&financier=${paramFinancier}`;
	}

	if (paramQ) {
		fetchLoansUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchLoans,
		isLoading: isLoadingLoans,
		refetch: refetchLoans,
	} = useFetch({
		isLazy: true,
		url: fetchLoansUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}

			if (res.pagination) {
				setPaginationLoans(res.pagination);
			}
		},
	});

	// fetch entities summary
	const { isLoading: isLoadingEntitiesSummary, refetch: refetchEntitiesSummary } = useFetch({
		isEnabled: !isWbg && isOnboarded && tabValue === 'entities',
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,type&type=AUDITOR,BORROWER,DONOR,INTERMEDIARY,PIU,SUB_PIU,TAX_AUTHORITY`,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setEntitiesSummary(res.data);
			}
		},
	});

	// fetch entities
	let fetchEntitiesUrl = `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&page=${paramPage}&limit=${paginationEntities.perPage}&populate=wallet,activities,activities.actor&order=${paramOrder}&orderBy=${paramOrderBy}`;

	if (paramQ) {
		fetchEntitiesUrl += `&q=${paramQ}`;
	}

	if (paramType) {
		fetchEntitiesUrl += `&type=${paramType}`;
	} else {
		fetchEntitiesUrl += `&type=AUDITOR,BORROWER,DONOR,INTERMEDIARY,PIU,SUB_PIU,TAX_AUTHORITY`;
	}

	const {
		fetchRequest: fetchEntities,
		isLoading: isLoadingEntities,
		refetch: refetchEntities,
	} = useFetch({
		isLazy: true,
		url: fetchEntitiesUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}

			if (res.pagination) {
				setPaginationEntities(res.pagination);
			}
		},
	});

	// fetch opex
	let fetchOpExUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&page=${paramPage}&limit=${paginationPayments.perPage}&type=OPEX&&populate=activities,activities.actor,category,component,comments.user,expenseType,fromAccount,fromEntity,payments.conversions,payments.loan,tags&orderBy=${paramOrderBy}&order=${paramOrder}`;

	if (paramCategory) {
		fetchOpExUrl += `&category=${paramCategory}`;
	}
	if (paramComponent) {
		fetchOpExUrl += `&component=${paramComponent}`;
	}
	if (paramExpenseType) {
		fetchOpExUrl += `&expenseType=${paramExpenseType}`;
	}
	if (paramFromEntity) {
		fetchOpExUrl += `&fromEntity=${paramFromEntity}`;
	}
	if (paramQ) {
		fetchOpExUrl += `&q=${paramQ}`;
	}
	if (paramTags) {
		fetchOpExUrl += `&tags=${paramTags}`;
	}

	const {
		fetchRequest: fetchOpEx,
		isLoading: isLoadingOpEx,
		refetch: refetchOpEx,
	} = useFetch({
		isLazy: true,
		url: fetchOpExUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setOpEx(res.data);
			}

			if (res.pagination) {
				setPaginationOpEx(res.pagination);
			}
		},
	});

	// fetch payments
	const populatePayments = [
		'activities',
		'activities.actor',
		'category',
		'component',
		'comments.user',
		'expenseType',
		'fromAccount',
		'fromEntity',
		'contract',
		'payments.conversions',
		'payments.loan',
		'refunds',
		'refunds.payments',
		'refunds.payments.conversions',
		'refunds.payments.loan',
		'refunds.toAccount',
		'tags',
		'toEntity',
	];

	let fetchPaymentsUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${
		project?.id
	}&page=${paramPage}&limit=${paginationPayments.perPage}&type=${
		paramType || (featureFlags['DIRECT'] ? 'DIRECT,PAYMENT' : 'PAYMENT')
	}&populate=${populatePayments.join(',')}&orderBy=${paramOrderBy}&order=${paramOrder}`;

	if (paramCategory) {
		fetchPaymentsUrl += `&category=${paramCategory}`;
	}
	if (paramComponent) {
		fetchPaymentsUrl += `&component=${paramComponent}`;
	}
	if (paramContract) {
		fetchPaymentsUrl += `&contract=${paramContract}`;
	}
	if (paramExpenseType) {
		fetchPaymentsUrl += `&expenseType=${paramExpenseType}`;
	}
	if (paramLoan) {
		fetchPaymentsUrl += `&loan=${paramLoan}`;
	}
	if (paramStatus) {
		fetchPaymentsUrl += `&status=${paramStatus}`;
	}
	if (paramTags) {
		fetchPaymentsUrl += `&tags=${paramTags}`;
	}
	if (paramToEntity) {
		fetchPaymentsUrl += `&toEntity=${paramToEntity}`;
	}
	if (paramQ) {
		fetchPaymentsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchPayments,
		isLoading: isLoadingPayments,
		refetch: refetchPayments,
	} = useFetch({
		isLazy: true,
		url: fetchPaymentsUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setPayments(res.data);
			}

			if (res.pagination) {
				setPaginationPayments(res.pagination);
			}
		},
	});

	// fetch refunds
	let fetchRefundsUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&type=PAYMENT_REFUND&populate=activities,activities.actor,comments.user,fromAccount,fromEntity,payments.conversions,payments.loan,tags,toAccount,toEntity,transactionRefunded&page=${paramPage}&limit=${paginationPaymentRefunds.perPage}&orderBy=${paramOrderBy}&order=${paramOrder}`;

	if (paramQ) {
		fetchRefundsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchPaymentRefunds,
		isLoading: isLoadingPaymentRefunds,
		refetch: refetchPaymentRefunds,
	} = useFetch({
		isLazy: true,
		url: fetchRefundsUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setPaymentRefunds(res.data);
			}

			if (res.pagination) {
				setPaginationPaymentRefunds(res.pagination);
			}
		},
	});

	// fetch reimbursements
	let fetchReimbursementsUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&type=REIMBURSEMENT&populate=activities,activities.actor,payments,payments.conversions,payments.loan,toAccount&fields=activities,conversions,created,currency,date,id,modified,payments,project,toAccount,transactions,type&page=${paramPage}&limit=${paginationReimbursements.perPage}`;

	if (paramQ) {
		fetchReimbursementsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchReimbursements,
		isLoading: isLoadingReimbursements,
		refetch: refetchReimbursements,
	} = useFetch({
		isEnabled: Boolean(project?.id) && featureFlags['REIMBURSEMENT'],
		url: fetchReimbursementsUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setReimbursements(res.data);
			}

			if (res.pagination) {
				setPaginationReimbursements(res.pagination);
			}
		},
	});

	// fetch special commitments
	let fetchSpecialCommitmentsUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${
		project?.id
	}&page=${paramPage}&limit=${paginationSpecialCommitments.perPage}&type=${
		paramType || 'SPECIAL'
	}&populate=activities,activities.actor,category,component,comments.user,expenseType,fromAccount,fromEntity,contract,payments.conversions,payments.loan,tags,toEntity&orderBy=${paramOrderBy}&order=${paramOrder}`;

	if (paramCategory) {
		fetchSpecialCommitmentsUrl += `&category=${paramCategory}`;
	}
	if (paramComponent) {
		fetchSpecialCommitmentsUrl += `&component=${paramComponent}`;
	}
	if (paramContract) {
		fetchSpecialCommitmentsUrl += `&contract=${paramContract}`;
	}
	if (paramExpenseType) {
		fetchSpecialCommitmentsUrl += `&expenseType=${paramExpenseType}`;
	}
	if (paramLoan) {
		fetchSpecialCommitmentsUrl += `&loan=${paramLoan}`;
	}
	if (paramStatus) {
		fetchSpecialCommitmentsUrl += `&status=${paramStatus}`;
	}
	if (paramTags) {
		fetchSpecialCommitmentsUrl += `&tags=${paramTags}`;
	}
	if (paramToEntity) {
		fetchSpecialCommitmentsUrl += `&toEntity=${paramToEntity}`;
	}
	if (paramQ) {
		fetchSpecialCommitmentsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchSpecialCommitments,
		isLoading: isLoadingSpecialCommitments,
		refetch: refetchSpecialCommitments,
	} = useFetch({
		isLazy: true,
		url: fetchSpecialCommitmentsUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setSpecialCommitments(res.data);
			}

			if (res.pagination) {
				setPaginationSpecialCommitments(res.pagination);
			}
		},
	});

	// fetch advances
	let fetchAdvancesUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&type=ADVANCE&populate=activities,activities.actor,payments,payments.conversions,payments.loan,toAccount&fields=activities,conversions,created,currency,date,id,modified,payments,project,toAccount,type&page=${paramPage}&limit=${paginationAdvances.perPage}`;

	if (paramQ) {
		fetchAdvancesUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchAdvances,
		isLoading: isLoadingAdvances,
		refetch: refetchAdvances,
	} = useFetch({
		isEnabled: Boolean(project?.id),
		url: fetchAdvancesUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setAdvances(res.data);
			}

			if (res.pagination) {
				setPaginationAdvances(res.pagination);
			}
		},
	});

	// fetch cash benefits
	let fetchCashBenefitsUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&page=${paramPage}&limit=${paginationPayments.perPage}&type=CASH_BENEFIT&populate=activities,activities.actor,category,component,comments.user,expenseType,fromAccount,fromEntity,payments.conversions,payments.loan,tags,toEntity&orderBy=${paramOrderBy}&order=${paramOrder}`;

	if (paramCategory) {
		fetchCashBenefitsUrl += `&category=${paramCategory}`;
	}
	if (paramComponent) {
		fetchCashBenefitsUrl += `&component=${paramComponent}`;
	}
	if (paramExpenseType) {
		fetchCashBenefitsUrl += `&expenseType=${paramExpenseType}`;
	}
	if (paramLoan) {
		fetchCashBenefitsUrl += `&loan=${paramLoan}`;
	}
	if (paramStatus) {
		fetchCashBenefitsUrl += `&status=${paramStatus}`;
	}
	if (paramToEntity) {
		fetchCashBenefitsUrl += `&toEntity=${paramToEntity}`;
	}
	if (paramQ) {
		fetchCashBenefitsUrl += `&q=${paramQ}`;
	}
	if (paramTags) {
		fetchCashBenefitsUrl += `&tags=${paramTags}`;
	}

	const {
		fetchRequest: fetchCashBenefits,
		isLoading: isLoadingCashBenefits,
		refetch: refetchCashBenefits,
	} = useFetch({
		isLazy: true,
		url: fetchCashBenefitsUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setCashBenefits(res.data);
			}

			if (res.pagination) {
				setPaginationCashBenefits(res.pagination);
			}
		},
	});

	// fetch non cash benefits
	let fetchNonCashBenefitsUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&page=${paramPage}&limit=${paginationPayments.perPage}&type=NON_CASH_BENEFIT&populate=activities,activities.actor,benefit,comments.user,fromEntity,tags,toEntity&orderBy=${paramOrderBy}&order=${paramOrder}`;

	if (paramStatus) {
		fetchNonCashBenefitsUrl += `&status=${paramStatus}`;
	}
	if (paramToEntity) {
		fetchNonCashBenefitsUrl += `&toEntity=${paramToEntity}`;
	}
	if (paramQ) {
		fetchNonCashBenefitsUrl += `&q=${paramQ}`;
	}
	if (paramTags) {
		fetchNonCashBenefitsUrl += `&tags=${paramTags}`;
	}

	const {
		fetchRequest: fetchNonCashBenefits,
		isLoading: isLoadingNonCashBenefits,
		refetch: refetchNonCashBenefits,
	} = useFetch({
		isLazy: true,
		url: fetchNonCashBenefitsUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setNonCashBenefits(res.data);
			}

			if (res.pagination) {
				setPaginationNonCashBenefits(res.pagination);
			}
		},
	});

	// fetch transfers
	let fetchTransfersUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&page=${paramPage}&limit=${paginationTransfers.perPage}&type=TRANSFER&type=TRANSFER&populate=activities,activities.actor,comments.user,fromAccount,fromAccount.entities,tags,toAccount,toAccount.entities,payments.loan&orderBy=${paramOrderBy}&order=${paramOrder}`;

	if (paramQ) {
		fetchTransfersUrl += `&q=${paramQ}`;
	}
	if (paramStatus) {
		fetchTransfersUrl += `&status=${paramStatus}`;
	}
	if (paramFromAccount) {
		fetchTransfersUrl += `&fromAccount=${paramFromAccount}`;
	}
	if (paramTags) {
		fetchTransfersUrl += `&tags=${paramTags}`;
	}
	if (paramToAccount) {
		fetchTransfersUrl += `&toAccount=${paramToAccount}`;
	}

	const {
		fetchRequest: fetchTransfers,
		isLoading: isLoadingTransfers,
		refetch: refetchTransfers,
	} = useFetch({
		isLazy: true,
		url: fetchTransfersUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setTransfers(res.data);
			}

			if (res.pagination) {
				setPaginationTransfers(res.pagination);
			}
		},
	});

	// fetch historical transactions
	let fetchHistoricalTransactionsUrl = `${process.env.REACT_APP_API_URL}/documents?project=${project?.id}&page=${paramPage}&limit=${paginationHistoricalTransactions.perPage}&category=HISTORICAL_TRANSACTIONS&orderBy=${paramOrderBy}&order=${paramOrder}&populate=user`;

	if (paramQ) {
		fetchHistoricalTransactionsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchHistoricalTransactions,
		isLoading: isLoadingHistoricalTransactions,
		refetch: refetchHistoricalTransactions,
	} = useFetch({
		isLazy: true,
		url: fetchHistoricalTransactionsUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setHistoricalTransactions(res.data);
			}

			if (res.pagination) {
				setPaginationHistoricalTransactions(res.pagination);
			}
		},
	});

	// fetch suppliers
	let fetchSuppliersUrl = `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&page=${paramPage}&limit=${paginationEntities.perPage}&type=SUPPLIER&populate=wallet,activities,activities.actor&order=${paramOrder}&orderBy=${paramOrderBy}`;

	if (paramQ) {
		fetchSuppliersUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchSuppliers,
		isLoading: isLoadingSuppliers,
		refetch: refetchSuppliers,
	} = useFetch({
		isLazy: true,
		url: fetchSuppliersUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setSuppliers(res.data);
			}

			if (res.pagination) {
				setPaginationSuppliers(res.pagination);
			}
		},
	});

	// fetch beneficiaries
	let fetchBeneficiariesUrl = `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&page=${paramPage}&limit=${paginationBeneficiaries.perPage}&type=BENEFICIARY&populate=activities,activities.actor,wallet&order=${paramOrder}&orderBy=${paramOrderBy}`;

	if (paramHasTransactions) {
		fetchBeneficiariesUrl += `&hasTransactions=${paramHasTransactions}`;
	}

	if (paramQ) {
		fetchBeneficiariesUrl += `&q=${paramQ}`;
	}

	if (paramSubtype) {
		fetchBeneficiariesUrl += `&subtype=${paramSubtype}`;
	}

	const {
		fetchRequest: fetchBeneficiaries,
		isLoading: isLoadingBeneficiaries,
		refetch: refetchBeneficiaries,
	} = useFetch({
		isLazy: true,
		url: fetchBeneficiariesUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setBeneficiaries(res.data);
			}

			if (res.pagination) {
				setPaginationBeneficiaries(res.pagination);
			}
		},
	});

	// fetch project documents beneficiaries
	let fetchDocumentsBeneficiariesUrl = `${process.env.REACT_APP_API_URL}/documents?project=${project?.id}&entityType=BENEFICIARY&page=${paramPage}&limit=${paginationBeneficiaries.perPage}&fields=created,entities,notarizationHash,id,name,transaction,type,user&populate=entities,transaction,type,user&order=${paramOrder}&orderBy=${paramOrderBy}`;

	if (paramQ) {
		fetchDocumentsBeneficiariesUrl += `&q=${paramQ}`;
	}

	if (paramType) {
		fetchDocumentsBeneficiariesUrl += `&type=${paramType}`;
	}

	if (paramEntity) {
		fetchDocumentsBeneficiariesUrl += `&entity=${paramEntity}`;
	}

	const {
		fetchRequest: fetchDocumentsBeneficiaries,
		isLoading: isLoadingDocumentsBeneficiaries,
		refetch: refetchDocumentsBeneficiaries,
	} = useFetch({
		isLazy: true,
		url: fetchDocumentsBeneficiariesUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setDocumentsBeneficiaries(res.data);
			}

			if (res.pagination) {
				setPaginationDocumentsBeneficiaries(res.pagination);
			}
		},
	});

	// fetch project documents suppliers
	let fetchDocumentsSuppliersUrl = `${process.env.REACT_APP_API_URL}/documents?project=${project?.id}&entityType=SUPPLIER&page=${paramPage}&limit=${paginationBeneficiaries.perPage}&fields=contract,created,entities,notarizationHash,id,name,transaction,type,user&populate=contract,entities,transaction,type,user&order=${paramOrder}&orderBy=${paramOrderBy}`;

	if (paramQ) {
		fetchDocumentsSuppliersUrl += `&q=${paramQ}&search=contract.num,name`;
	}

	if (paramType) {
		fetchDocumentsSuppliersUrl += `&type=${paramType}`;
	}

	if (paramEntity) {
		fetchDocumentsSuppliersUrl += `&entity=${paramEntity}`;
	}

	if (paramContract) {
		fetchDocumentsSuppliersUrl += `&contract=${paramContract}`;
	}

	const {
		fetchRequest: fetchDocumentsSuppliers,
		isLoading: isLoadingDocumentsSuppliers,
		refetch: refetchDocumentsSuppliers,
	} = useFetch({
		isLazy: true,
		url: fetchDocumentsSuppliersUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setDocumentsSuppliers(res.data);
			}

			if (res.pagination) {
				setPaginationDocumentsSuppliers(res.pagination);
			}
		},
	});

	// fetch contracts
	let fetchContractsUrl = `${process.env.REACT_APP_API_URL}/contracts?project=${project?.id}&page=${paramPage}&limit=${paginationContracts.perPage}&populate=entities,activities,activities.actor&order=${paramOrder}&orderBy=${paramOrderBy}`;

	if (paramEntities) {
		fetchContractsUrl += `&entities=${paramEntities}`;
	}

	if (paramQ) {
		fetchContractsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchContracts,
		isLoading: isLoadingContracts,
		refetch: refetchContracts,
	} = useFetch({
		isLazy: true,
		url: fetchContractsUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setContracts(res.data);
			}

			if (res.pagination) {
				setPaginationContracts(res.pagination);
			}
		},
	});

	// fetch acts
	let fetchActsUrl = `${process.env.REACT_APP_API_URL}/acts?project=${project?.id}&page=${paramPage}&limit=${paginationActs.perPage}&order=${paramOrder}&orderBy=${paramOrderBy}&populate=activities.actor`;

	if (paramQ) {
		fetchActsUrl += `&q=${paramQ}`;
	}

	if (paramStatus) {
		fetchActsUrl += `&status=${paramStatus}`;
	}

	const {
		fetchRequest: fetchActs,
		isLoading: isLoadingActs,
		refetch: refetchActs,
	} = useFetch({
		isLazy: true,
		url: fetchActsUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setActs(res.data);
			}

			if (res.pagination) {
				setPaginationActs(res.pagination);
			}
		},
	});

	// fetch activities
	let fetchActivitiesUrl = `${process.env.REACT_APP_API_URL}/activities?project=${
		project?.id
	}&type=!PAYMENT_CREATED&populate=${paramPopulateActivities}&page=${paramPage}&limit=${
		paginationActivities.perPage
	}&order=${paramOrder}&orderBy=${paramOrderBy || 'created'}`;

	if (paramQ) {
		fetchActivitiesUrl += `&q=${paramQ}`;
	}

	if (paramType) {
		fetchActivitiesUrl += `&type=${paramType}`;
	}

	if (paramActor) {
		fetchActivitiesUrl += `&actor=${paramActor}`;
	}

	const {
		fetchRequest: fetchActivities,
		isLoading: isLoadingActivities,
		refetch: refetchActivities,
	} = useFetch({
		isLazy: true,
		url: fetchActivitiesUrl,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setActivities(res.data);
			}

			if (res.pagination) {
				setPaginationActivities(res.pagination);
			}
		},
	});

	// fetch users permissions
	const {
		fetchRequest: fetchUsersPermissions,
		isLoading: isLoadingUsersPermissions,
		refetch: refetchUsersPermissions,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/users?projects=${project?.id}&projects.permissions=beneficiary.readPii&populate=projects.permissions&page=${paramPage}&limit=${paginationUsersPermissions.perPage}`,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setUsersPermissions(res.data);
			}

			if (res.pagination) {
				setPaginationUsersPermissions(res.pagination);
			}
		},
	});

	// fetch plans
	let urlFetchIfrsPlans = `${process.env.REACT_APP_API_URL}/ifrs/plans?project=${project?.id}&populate=activities,activities.actor,versions&page=${paramPage}&limit=${paginationIfrPlans.perPage}`;

	if (paramQ) {
		urlFetchIfrsPlans += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchPlans,
		isLoading: isLoadingIfrPlans,
		refetch: refetchIfrPlans,
	} = useFetch({
		isLazy: true,
		url: urlFetchIfrsPlans,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setIfrPlans(res.data);
			}

			if (res.pagination) {
				setPaginationIfrPlans(res.pagination);
			}
		},
	});

	// fetch plan
	const {
		fetchRequest: fetchPlan,
		isLoading: isLoadingIfrPlan,
		refetch: refetchIfrPlan,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/ifrs/plans/${planId}?populate=activities,activities.actor,versions,versions.data`,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				// if fetched plan has changed
				if (res.data.id !== ifrPlan?.id) {
					// set most recent version
					const versionsSorted = [...res.data.versions].sort(
						(a: IfrPlanVersion, b: IfrPlanVersion) =>
							new Date(a.modified || a.created) < new Date(b.modified || b.created) ? 1 : -1
					);
					setIfrPlanVersion(versionsSorted[0]);
				}

				setIfrPlan(res.data);
			}
		},
	});

	// fetch ifrs
	let urlFetchIfrs = `${process.env.REACT_APP_API_URL}/ifrs/reports?project=${project?.id}&page=${paramPage}&limit=${paginationIfrs.perPage}&order=${paramOrder}&orderBy=${paramOrderBy}&status=!DELETED&populate=activities,activities.actor,ifrPlan`;

	if (paramQ) {
		urlFetchIfrs += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchIfrs,
		isLoading: isLoadingIfrs,
		refetch: refetchIfrs,
	} = useFetch({
		isLazy: true,
		url: urlFetchIfrs,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res.data) {
				setIfrs(res.data);
			}
			if (res.pagination) {
				setPaginationIfrs(res.pagination);
			}
		},
	});

	// fetch ifr
	const {
		fetchRequest: fetchIfr,
		isLoading: isLoadingIfr,
		refetch: refetchIfr,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/ifrs/reports/${idIfr}?project=${project?.id}&populate=activities,activities.actor,bankBalances,forecasts,forecastDeductions,ifrPlan,reconciliationItems`,
		options: fetchOptions,
		onSuccess: (res) => {
			if (res?.data) {
				setIfr(res.data[0]);
			}
		},
	});

	const tabs: TabProps[] = [
		{
			component: RouterLink,
			label: t('overview'),
			to: `/project/${id}/overview`,
			value: 'overview',
		},
	];

	if (!isOnboarded || (isOnboarded && userProjectPermissions?.loan?.read)) {
		tabs.push({
			component: RouterLink,
			label: t('financing'),
			to: `/project/${id}/financing`,
			value: 'financing',
		});
	}

	if (!isOnboarded || (isOnboarded && userProjectPermissions?.entity?.read)) {
		tabs.push({
			component: RouterLink,
			label: t('entities'),
			to: `/project/${id}/entities`,
			value: 'entities',
		});
	}

	if (!isOnboarded || (isOnboarded && userProjectPermissions?.account?.read)) {
		tabs.push({
			component: RouterLink,
			label: t('accounts'),
			to: `/project/${id}/accounts`,
			value: 'accounts',
		});
	}

	if (isOnboarded && project?.version === '2' && userProjectPermissions?.account?.read) {
		tabs.push({
			component: RouterLink,
			label: t('spendPlans'),
			to: `/project/${id}/spend-plans`,
			value: 'spend-plans',
		});
	}

	// "Invoices for Processing" — shown when user has verification section assignments
	if (isOnboarded && project?.version === '2' && hasSectionAssignments) {
		tabs.push({
			component: RouterLink,
			label: 'Invoices for Processing',
			to: `/project/${id}/invoices-for-processing`,
			value: 'invoices-for-processing',
		});
	}

	if (isOnboarded) {
		if (project?.version !== '2' && userProjectPermissions?.supplier?.read) {
			tabs.push({
				component: RouterLink,
				label: t('suppliers'),
				to: `/project/${id}/suppliers`,
				value: 'suppliers',
			});
		}

		if (project?.version !== '2' && userProjectPermissions?.contract?.read) {
			tabs.push({
				component: RouterLink,
				label: t('contracts'),
				to: `/project/${id}/contracts`,
				value: 'contracts',
			});
		}

		if (project?.version !== '2' && userProjectPermissions?.beneficiary?.read) {
			tabs.push({
				component: RouterLink,
				label: t('beneficiaries'),
				to: `/project/${id}/beneficiaries`,
				value: 'beneficiaries',
			});
		}

		if (project?.version !== '2' && hasTransactionsReadPermissions) {
			tabs.push({
				component: RouterLink,
				label: t('transactions'),
				to: `/project/${id}/transactions`,
				value: 'transactions',
			});
		}

		if (userProjectPermissions?.act?.read && featureFlags['ACTIVITIES']) {
			tabs.push({
				component: RouterLink,
				label: t('activities'),
				to: `/project/${id}/activities`,
				value: 'activities',
			});
		}

		if (hasTransactionsReadPermissions) {
			tabs.push({
				'data-tour': 'reports',
				component: RouterLink,
				label: t('reports'),
				to: `/project/${id}/reports`,
				value: 'reports',
			});
		}

		if (
			user?.role === 'WFA' ||
			user?.role === 'FNFA' ||
			(userEntity && !entityTypesReadOnly.some((type) => type === userEntity?.type))
		) {
			tabs.push({
				component: RouterLink,
				icon: <IconSettings />,
				label: t('settings'),
				sx: {
					marginLeft: 'auto',
				},
				to: `/project/${id}/settings`,
				value: 'settings',
			});
		}
	} else {
		// add isWbg param to each tab link indicating project has not yet been onboarded
		tabs.forEach((tab) => {
			tab.to = `${tab.to}?isWbg=true`;
		});
	}

	const activeTab = tabs.find((tab) => tab.value === tabValue);

	const handleCloseDialogOnboardProject = (
		e: MouseEvent<HTMLButtonElement | HTMLAnchorElement | HTMLFormElement>
	) => {
		searchParams.delete('dialog');
		setSearchParams(searchParams);
	};

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleOnboardingSuccess = () => {
		// remove isWbg param
		searchParams.delete('isWbg');
		setSearchParams(searchParams);

		// set onboard success banner
		setBanner({
			hasClose: true,
			message: t('alert.onboardSuccessProject'),
			severity: 'success',
		});
	};

	const titleActions: ActionButton[] = [];

	if (isOnboarded && !isStaff) {
		if (tabValue === 'accounts' && userProjectPermissions?.account?.create) {
			titleActions.push({
				children: t('addAccount'),
				onClick: () => {
					searchParams.set('dialog', 'addAccount');
					if (pathname.includes('local')) {
						searchParams.set('dialogType', 'LOCAL');
					}
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		} else if (
			tabValue === 'acts' &&
			userProjectPermissions?.contract?.create &&
			userProjectPermissions?.entity?.create
		) {
			titleActions.push({
				children: t('addActivity'),
				onClick: () => {
					searchParams.set('dialog', 'addActivity');
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		} else if (tabValue === 'contracts' && userProjectPermissions?.contract?.create) {
			titleActions.push({
				children: t('addContract'),
				onClick: () => {
					searchParams.set('dialog', 'addContract');
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		} else if (tabValue === 'entities' && userProjectPermissions?.entity?.create) {
			titleActions.push({
				children: t('addEntity'),
				onClick: () => {
					searchParams.set('dialog', 'addEntity');
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		} else if (tabValue === 'financing' && userProjectPermissions?.loan?.create) {
			titleActions.push({
				children: t('addFinancingSource'),
				onClick: () => {
					searchParams.set('dialog', 'addFinancingSource');
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		} else if (pathname.includes('plan/')) {
			if (hasLoansEnabled && ifrPlanVersion?.status === 'DRAFT') {
				titleActions.push(
					{
						children: t('saveDraft'),
						onClick: () => {
							patchIfrPlanVersion({
								ifrPlanId: ifrPlan?.id,
								ifrPlanVersionId: ifrPlanVersion?.id,
								ifrPlanVersionPayload,
								onSuccess: ({ ifrPlanVersion: ifrPlanVersionUpdated }) => {
									// refetch ifr plan
									refetchIfrPlan();

									// set ifr plan version
									setIfrPlanVersion(ifrPlanVersionUpdated);
								},
							});
						},
						variant: 'outlined',
					},
					{
						children: t('finalize'),
						onClick: () => {
							searchParams.set('dialog', 'updateIfrPlanVersionStatus');
							searchParams.set('dialogStatus', 'FINALIZED');
							setSearchParams(searchParams);
						},
						variant: 'contained',
					}
				);
			} else if (ifrPlanVersion?.status === 'FINALIZED') {
				const hasDraft = ifrPlan?.versions?.some((version) => version.status === 'DRAFT');
				if (!hasDraft) {
					titleActions.push({
						children: t('editPlan'),
						onClick: () => {
							// Show create version dialog
							searchParams.set('dialog', 'editPlan');
							setSearchParams(searchParams);
						},
						variant: 'contained',
					});
				}
			}
		} else if (tabValue === 'suppliers' && userProjectPermissions?.supplier?.create) {
			if (!pathname.includes('documents') && userProjectPermissions?.supplier?.create) {
				const actions: ActionButton[] = [];

				actions.push(
					{
						children: t('addSupplier'),
						onClick: () => {
							searchParams.set('dialog', 'addSupplier');
							setSearchParams(searchParams);
						},
						variant: 'contained',
					},
					{
						children: t('uploadSuppliers'),
						onClick: () => {
							searchParams.set('dialog', 'uploadSuppliers');
							setSearchParams(searchParams);
						},
						variant: 'contained',
					}
				);

				if (actions.length) {
					titleActions.push({
						BtnProps: {
							actions,
						},
					});
				}
			}
		} else if (tabValue === 'beneficiaries' && userProjectPermissions?.beneficiary?.create) {
			const actions: ActionButton[] = [];

			if (userProjectPermissions?.beneficiary?.create) {
				actions.push(
					{
						children: t('addBeneficiary'),
						onClick: () => {
							searchParams.set('dialog', 'addBeneficiary');
							setSearchParams(searchParams);
						},
						variant: 'contained',
					},
					{
						children: t('uploadBeneficiaries'),
						onClick: () => {
							searchParams.set('dialog', 'uploadBeneficiaries');
							setSearchParams(searchParams);
						},
						variant: 'contained',
					}
				);
			}

			if (userProjectPermissions?.cashBenefit?.create) {
				actions.push({
					children: t('uploadTransactions'),
					onClick: () => {
						searchParams.set('dialog', 'uploadTransactions');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			}

			if (actions.length) {
				titleActions.push({
					BtnProps: {
						actions,
					},
				});
			}
		} else if (tabValue === 'settings') {
			if (pathname.includes('form/')) {
				titleActions.push({
					children: t('saveForm'),
					onClick: () => {
						searchParams.set('dialog', 'updateForm');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			} else if (pathname.includes('/settings/project')) {
				titleActions.push({
					children: t('saveProject'),
					form: 'form-edit-project',
					type: 'submit',
					variant: 'contained',
				});
			} else if (
				pathname.includes('/expense-types') &&
				userProjectPermissions?.expenseType?.update
			) {
				titleActions.push({
					children: t('saveExpenseTypes'),
					onClick: () => {
						searchParams.set('dialog', 'updateExpenseTypes');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			} else if (pathname.includes('/notifications')) {
				titleActions.push({
					children: t('saveNotifications'),
					onClick: () => {
						searchParams.set('dialog', 'updateUserNotifications');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			} else if (pathname.includes('/categories') && userProjectPermissions?.category?.create) {
				titleActions.push({
					children: t('addCategory'),
					onClick: () => {
						searchParams.set('dialog', 'addCategory');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			} else if (
				pathname.includes('/cash-in-methods') &&
				userProjectPermissions?.cashInMethod?.create
			) {
				titleActions.push({
					children: t('addCashInMethod'),
					onClick: () => {
						searchParams.set('dialog', 'addCashInMethod');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			} else if (pathname.includes('/components') && userProjectPermissions?.component?.create) {
				titleActions.push({
					children: t('addComponent'),
					onClick: () => {
						searchParams.set('dialog', 'addComponent');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			} else if (
				pathname.includes('/reconciliation-items') &&
				userProjectPermissions?.reconciliationItem?.create
			) {
				titleActions.push({
					children: t('addReconciliationItem'),
					onClick: () => {
						searchParams.set('dialog', 'addReconciliationItem');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			}
		} else if (tabValue === 'transactions' && project?.version !== '2') {
			// projects render the Transactions screen read-only — payments are
			// created via the v2 prepare-payment flow, so suppress all "Record …"
			// header CTAs. See BLDT/docs/v2-payment-architecture.md.
			const actions: ActionButton[] = [];

			if (userProjectPermissions?.payment?.create) {
				actions.push({
					children: t('recordPayment'),
					startIcon: <IconPaid />,
					to: `/project/${id}/transactions/payments?dialog=recordPayment`,
					variant: 'contained',
				});
			}

			if (userProjectPermissions?.cashBenefit?.create) {
				actions.push({
					children: t('recordCashBenefit'),
					startIcon: <IconPaid />,
					to: `/project/${id}/transactions/cash-benefits?dialog=recordCashBenefit`,
					variant: 'contained',
				});
			}

			if (userProjectPermissions?.nonCashBenefit?.create) {
				actions.push({
					children: t('recordNonCashBenefit'),
					startIcon: <IconVolunteerActivism />,
					to: `/project/${id}/transactions/non-cash-benefits?dialog=recordNonCashBenefit`,
					variant: 'contained',
				});
			}

			if (userProjectPermissions?.opEx?.create) {
				actions.push({
					children: t('recordOpEx'),
					startIcon: <IconLocalAtm />,
					to: `/project/${id}/transactions/opex?dialog=recordOpEx`,
					variant: 'contained',
				});
			}

			if (userProjectPermissions?.transfer?.create) {
				actions.push({
					children: t('recordFundTransfer'),
					startIcon: <IconTransfer />,
					to: `/project/${id}/transactions/transfers?dialog=recordFundTransfer`,
					variant: 'contained',
				});
			}

			if (userProjectPermissions?.document?.create) {
				actions.push({
					children: t('uploadHistoricalTransactions'),
					startIcon: <IconInsertDriveFile />,
					to: `/project/${id}/transactions/historical?dialog=uploadHistoricalTransactions`,
					variant: 'contained',
				});
			}

			if (actions.length > 0) {
				titleActions.push({
					BtnProps: {
						actions,
						btnMain: {
							children: t('recordTransaction'),
							variant: 'contained',
						},
					},
				});
			}
		} else if (tabValue === 'reports') {
			if (!hasOnlyIfrTrials && pathname.includes('financial/plans') && !planId) {
				titleActions.push({
					children: t('addPlan'),
					onClick: () => {
						searchParams.set('dialog', 'addPlan');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			} else if (pathname.includes('/ifr/')) {
				if (idIfr && ifr) {
					const isIfrTrial = ifr.status === 'TRIAL';

					if (hasLoansEnabled && !isLoadingIfr) {
						if (['DRAFT', 'TRIAL'].includes(ifr.status)) {
							const btnSaveReport: ActionButton = {
								children: t('saveReport'),
								onClick: () => {
									patchIfr({
										ifrId: ifr?.id,
										ifrPayload,
										onSuccess: () => {
											console.log('Success');
										},
									});
								},
								variant: 'outlined',
							};

							const btnFinalize: ActionButton = {
								children: t('finalize'),
								onClick: () => {
									searchParams.set('dialog', 'updateIfrStatus');
									searchParams.set('dialogStatus', 'FINALIZED');
									setSearchParams(searchParams);
								},
								variant: 'contained',
							};

							if (isIfrTrial || hasOnlyIfrTrials) {
								const btnTooltip: Omit<TooltipProps, 'children'> = {
									arrow: true,
									placement: 'top',
									title: t('unauthorized'),
								};

								if (isIfrTrial) {
									btnTooltip.title = t('unavailableInTrial');
								}

								btnSaveReport.disabled = true;
								btnFinalize.disabled = true;

								btnSaveReport.TooltipProps = btnTooltip;
								btnFinalize.TooltipProps = btnTooltip;
							}

							titleActions.push(btnSaveReport, btnFinalize);
						}
					}
				}
			} else if (
				!pathname.includes('financial/plans') &&
				(pathname.includes('reports/financial') || pathname.includes('financial/reports'))
			) {
				const btnAddReport: ActionButton = {
					children: t('addReport'),
					onClick: () => {
						searchParams.set('dialog', 'addReport');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				};

				if (hasOnlyIfrTrials) {
					btnAddReport.children = t('addTrialReport');
				}

				titleActions.push(btnAddReport);
			}
		}
	}

	const emptyActions: ActionButton[] = [];

	if (user?.role === 'WFA') {
		emptyActions.push({
			component: RouterLink,
			children: t('onboardProject'),
			to: '/projects/onboard',
			variant: 'contained',
		});
	}

	// set onboard banner
	useEffect(() => {
		if (isWbg && project?.status === 'PENDING') {
			setBanner({
				actions: [
					{
						component: RouterLink,
						color: 'error',
						children: t('cancel'),
						to: '/projects/onboard',
						variant: 'contained',
					},
					{
						color: 'primary',
						children: t('onboard'),
						onClick: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
							searchParams.set('dialog', 'onboardProject');
							setSearchParams(searchParams);
						},
						variant: 'contained',
					},
				],
				message: t('alert.onboardToEnableLoans'),
			});
		}
	}, [isWbg, project?.id, project?.status, searchParams, setSearchParams, setBanner, t]);

	// fetch paginated resources
	useEffect(() => {
		if (project?.id) {
			if (tabValue === 'overview') {
				fetchActivities();
				fetchLoans();
			} else if (tabValue === 'accounts') {
				if (pathname.includes('local')) {
					fetchAccountsLocal();
				} else {
					fetchAccountsDesignated();
				}
			} else if (tabValue === 'activities') {
				fetchActivities();
			} else if (tabValue === 'contracts') {
				fetchContracts();
			} else if (tabValue === 'entities') {
				fetchEntities();
			} else if (tabValue === 'financing') {
				fetchLoans();
			} else if (tabValue === 'transactions') {
				if (pathname.includes('non-cash-benefits')) {
					fetchNonCashBenefits();
				} else if (pathname.includes('special')) {
					fetchSpecialCommitments();
				} else if (pathname.includes('benefits')) {
					fetchCashBenefits();
				} else if (pathname.includes('historical')) {
					fetchHistoricalTransactions();
				} else if (pathname.includes('opex')) {
					fetchOpEx();
				} else if (pathname.includes('advances')) {
					fetchAdvances();
				} else if (pathname.includes('reimbursements')) {
					fetchReimbursements();
				} else if (pathname.includes('refunds')) {
					fetchPaymentRefunds();
				} else if (pathname.includes('transfers')) {
					fetchTransfers();
				} else {
					fetchPayments();
				}
			} else if (tabValue === 'reports') {
				if (idIfr) {
					fetchLoans();
					fetchIfr();
				} else if (planId) {
					fetchLoans();
					fetchPlan();
				} else if (pathname.includes('financial')) {
					if (pathname.includes('financial/plans')) {
						fetchPlans();
					} else {
						fetchIfrs();
					}
				}
			} else if (tabValue === 'suppliers') {
				fetchLoans();
				fetchEntities();
				if (pathname.includes('documents')) {
					fetchDocumentsSuppliers();
				} else {
					fetchSuppliers();
				}
			} else if (tabValue === 'beneficiaries') {
				fetchLoans();
				fetchEntities();
				if (pathname.includes('documents')) {
					fetchDocumentsBeneficiaries();
				} else {
					fetchBeneficiaries();
				}
			} else if (tabValue === 'plans') {
				if (planId) {
					fetchLoans();
					fetchPlan();
				} else {
					fetchPlans();
				}
			} else if (tabValue === 'spend-plans') {
				fetchLoans();
				fetchUsersPermissions();
				fetchEntities();
				// Load suppliers and beneficiaries for all spend-plan pages (needed for bulk upload dialogs)
				if (pathname.includes('suppliers')) {
					if (pathname.includes('documents')) {
						fetchDocumentsSuppliers();
					} else {
						fetchSuppliers();
					}
				} else if (pathname.includes('beneficiaries')) {
					if (pathname.includes('documents')) {
						fetchDocumentsBeneficiaries();
					} else {
						fetchBeneficiaries();
					}
				} else {
					// For other spend-plan pages (contracts, work-plans, etc.), load both suppliers and beneficiaries
					fetchSuppliers();
					fetchBeneficiaries();
				}
			} else if (tabValue === 'settings') {
				fetchUsersPermissions();
			}
		}
	}, [
		fetchAccountsDesignated,
		fetchAccountsLocal,
		fetchActivities,
		fetchActs,
		fetchAdvances,
		fetchBeneficiaries,
		fetchCashBenefits,
		fetchContracts,
		fetchNonCashBenefits,
		fetchDocumentsBeneficiaries,
		fetchDocumentsSuppliers,
		fetchEntities,
		fetchIfr,
		fetchIfrs,
		fetchLoans,
		fetchOpEx,
		fetchPayments,
		fetchPlan,
		fetchPlans,
		fetchPaymentRefunds,
		fetchSpecialCommitments,
		fetchReimbursements,
		fetchSuppliers,
		fetchTransfers,
		fetchHistoricalTransactions,
		fetchUsersPermissions,
		idIfr,
		pathname,
		paramActor,
		paramBeneficiary,
		paramCategory,
		paramComponent,
		paramContract,
		paramEntities,
		paramEntity,
		paramExpenseType,
		paramFinancier,
		paramFromAccount,
		paramFromEntity,
		paramHasTransactions,
		paramLoan,
		paramOrder,
		paramOrderBy,
		paramPage,
		paramQ,
		paramStatus,
		paramSubtype,
		paramSupplier,
		paramTags,
		paramToAccount,
		paramToEntity,
		paramType,
		planId,
		project?.id,
		tabValue,
	]);

	// set active tab
	useEffect(() => {
		const tabValueByLocation = getTabByLocation(pathname);
		setTabValue(tabValueByLocation);
	}, [getTabByLocation, pathname]);

	// remove banner when component is unmounted (preserve global banners)
	useEffect(() => {
		return () => {
			setBanner((bannerPrev) => (bannerPrev?.isGlobal ? bannerPrev : null));
		};
	}, [setBanner]);

	// set user entity when project changes
	useEffect(() => {
		let entities = user?.entities as Entity[];

		if (project?.id) {
			// filter user entities by project
			entities = entities.filter((entity) =>
				Boolean((entity.projects as number[])?.find((id) => id === project.id))
			);

			if (entities.length) {
				sessionStorage.setItem('userEntityId', String(entities[0]?.id));
				setUserEntity(entities[0]);
			}
		}
	}, [project?.id, setUserEntity, user]);

	const isLoadingProjectOrWbgProject = isLoadingProject || isLoadingWbgProject;

	const renderTemplate = () => {
		if (isLoadingProjectOrWbgProject) {
			if (isLoadingProject) {
				return <Loading variant="page" />;
			}
			return <Empty isLoading={true} variant="page" />;
		}

		const segments = pathname.split('/');
		const outletOnlySegments = ['account', 'beneficiary', 'entity', 'supplier'];
		const isSpendPlanRoute = segments.includes('spend-plans');

		if (
			(!isSpendPlanRoute &&
				outletOnlySegments.filter((segment) => segments.includes(segment)).length > 0) ||
			isHeadless
		) {
			return <Outlet />;
		}

		if (project) {
			let urlProject = `/project/${project.idWbg}`;

			if (isWbg) {
				urlProject += `?isWbg=true`;
			}

			const breadcrumbsProps: BreadcrumbsProps = {
				crumbs: [
					{
						children: t('projects'),
						to: '/projects',
					},
					{
						children: project.idWbg,
						to: urlProject,
					},
				],
				page: String(activeTab?.label),
			};

			// set breadcrumbs for sub tabs
			// Check spend-plans FIRST since its sub-routes contain patterns like 'activities', 'contracts'
			if (pathname.includes('spend-plans')) {
				breadcrumbsProps.crumbs.push({
					children: 'FundsChain 2.0',
					to: `${urlProject}/spend-plans`,
				});

				// Check for detail page routes first (more specific patterns)
				if (/\/activity\/\d+/.test(pathname)) {
					breadcrumbsProps.crumbs.push({
						children: t('activities'),
						to: `${urlProject}/spend-plans/activities`,
					});
					breadcrumbsProps.page = t('activityDetails');
				} else if (/\/condition\/\d+/.test(pathname)) {
					breadcrumbsProps.crumbs.push({
						children: t('conditions'),
						to: `${urlProject}/spend-plans/conditions`,
					});
					breadcrumbsProps.page = t('spendPlan.conditionDetails');
				} else if (/\/contract\/\d+/.test(pathname)) {
					breadcrumbsProps.crumbs.push({
						children: t('contracts'),
						to: `${urlProject}/spend-plans/contracts`,
					});
					breadcrumbsProps.page = t('contractDetails');
				} else if (pathname.includes('components-categories')) {
					breadcrumbsProps.page = t('components');
				} else if (pathname.includes('custom-templates')) {
					breadcrumbsProps.page = t('spendPlan.customTemplates');
				} else if (pathname.includes('dashboard')) {
					breadcrumbsProps.page = t('spendPlan.planningSummary');
				} else if (pathname.includes('documents')) {
					breadcrumbsProps.page = t('spendPlan.documents.allDocuments');
				} else if (pathname.includes('funding-earmarks')) {
					breadcrumbsProps.page = t('spendPlan.fundingEarmarks');
				} else if (pathname.includes('chat')) {
					breadcrumbsProps.page = t('spendPlan.aiChat');
				} else if (pathname.includes('beneficiaries')) {
					breadcrumbsProps.page = t('beneficiaries');
				} else if (pathname.includes('invoices')) {
					breadcrumbsProps.page = t('spendPlan.invoices');
				} else if (pathname.includes('payments')) {
					breadcrumbsProps.page = t('payments');
				} else if (pathname.includes('spend-planning')) {
					breadcrumbsProps.page = t('spendPlan.spendPlanning');
				} else if (pathname.includes('suppliers')) {
					breadcrumbsProps.page = t('suppliers');
				} else if (pathname.includes('verification-groups')) {
					breadcrumbsProps.page = t('spendPlan.verificationGroups');
				} else if (pathname.includes('work-planning')) {
					breadcrumbsProps.page = t('spendPlan.workPlanning');
				} else if (pathname.includes('work-plans')) {
					breadcrumbsProps.page = t('workPlans');
				} else if (/\/voucher\/\d+/.test(pathname)) {
					breadcrumbsProps.crumbs.push({
						children: t('payments'),
						to: `${urlProject}/spend-plans/vouchers`,
					});
					breadcrumbsProps.page = t('spendPlan.voucherDetails');
				} else if (pathname.includes('activities')) {
					breadcrumbsProps.page = t('activities');
				} else if (pathname.includes('conditions')) {
					breadcrumbsProps.page = t('conditions');
				} else if (pathname.includes('contracts')) {
					breadcrumbsProps.page = t('contracts');
				} else if (/\/work-plan\/\d+/.test(pathname)) {
					breadcrumbsProps.crumbs.push({
						children: t('workPlans'),
						to: `${urlProject}/spend-plans/work-planning`,
					});
					breadcrumbsProps.page = t('spendPlan.workPlanDetails');
				} else {
					breadcrumbsProps.page = t('spendPlan.planningSummary');
				}
			} else if (pathname.includes('activities')) {
				breadcrumbsProps.page = t('activities');
			} else if (pathname.includes('beneficiaries/')) {
				breadcrumbsProps.crumbs.push({
					children: t('beneficiaries'),
					to: `${urlProject}/beneficiaries`,
				});

				if (pathname.includes('details')) {
					breadcrumbsProps.page = t('details');
				} else if (pathname.includes('documents')) {
					breadcrumbsProps.page = t('documents');
				}
			} else if (pathname.includes('settings/')) {
				breadcrumbsProps.crumbs.push({
					children: t('settings'),
					to: `${urlProject}/settings`,
				});
				if (pathname.includes('benefits')) {
					breadcrumbsProps.page = t('nonCashBenefits');
				} else if (pathname.includes('cash-in-methods')) {
					breadcrumbsProps.page = t('cashInMethods');
				} else if (pathname.includes('categories')) {
					breadcrumbsProps.page = t('categories');
				} else if (pathname.includes('components')) {
					breadcrumbsProps.page = t('components');
				} else if (pathname.includes('custom-fields')) {
					breadcrumbsProps.page = t('customFields');
				} else if (pathname.includes('expense-types')) {
					breadcrumbsProps.page = t('expenseTypes');
				} else if (pathname.includes('form')) {
					breadcrumbsProps.page = t('forms');
				} else if (pathname.includes('ifr')) {
					if (idIfr) {
						breadcrumbsProps.crumbs.push({
							children: t('ifrs'),
							to: `${urlProject}/reports/financial`,
						});
					}
				} else if (pathname.includes('notifications')) {
					breadcrumbsProps.page = t('notifications');
				} else if (pathname.includes('permissions')) {
					breadcrumbsProps.page = t('permissions');
				} else if (pathname.includes('program')) {
					breadcrumbsProps.page = t('program');
				} else if (pathname.includes('reconciliation-items')) {
					breadcrumbsProps.page = t('reconciliationItems');
				} else if (pathname.includes('settings/project')) {
					breadcrumbsProps.page = t('project');
				} else if (pathname.includes('tags')) {
					breadcrumbsProps.page = t('tags');
				}
			} else if (pathname.includes('suppliers/')) {
				breadcrumbsProps.crumbs.push({
					children: t('suppliers'),
					to: `${urlProject}/suppliers`,
				});

				if (pathname.includes('details')) {
					breadcrumbsProps.page = t('details');
				} else if (pathname.includes('documents')) {
					breadcrumbsProps.page = t('documents');
				}
			} else if (pathname.includes('transactions')) {
				// Always add transactions as a crumb for all transactions paths
				breadcrumbsProps.crumbs.push({
					children: t('transactions'),
					to: `/project/${project.idWbg}/transactions/payments${isWbg ? '?isWbg=true' : ''}`,
				});

				if (pathname.includes('advances')) {
					breadcrumbsProps.page = t('advances');
				} else if (pathname.includes('/cash-benefits')) {
					breadcrumbsProps.page = t('cashBenefits');
				} else if (pathname.includes('historical')) {
					breadcrumbsProps.page = t('historicalTransactions');
				} else if (pathname.includes('/non-cash-benefits')) {
					breadcrumbsProps.page = t('nonCashBenefits');
				} else if (pathname.includes('opex')) {
					breadcrumbsProps.page = t('operatingExpenses');
				} else if (pathname.includes('payment-refunds')) {
					breadcrumbsProps.page = t('paymentRefunds');
				} else if (pathname.includes('payments')) {
					breadcrumbsProps.page = t('payments');
				} else if (pathname.includes('reimbursements')) {
					breadcrumbsProps.page = t('reimbursements');
				} else if (pathname.includes('special')) {
					breadcrumbsProps.page = t('specialCommitments');
				} else if (pathname.includes('transfers')) {
					breadcrumbsProps.page = t('transfers');
				}
			} else if (
				pathname.includes('flow') ||
				pathname.includes('ifr') ||
				pathname.includes('plan') ||
				pathname.includes('reports')
			) {
				breadcrumbsProps.crumbs.push({
					children: t('reports'),
					to: `${urlProject}/reports`,
				});

				if (pathname.includes('flow')) {
					breadcrumbsProps.page = t('fundFlow');
				} else if (
					pathname.includes('financial/plans') ||
					pathname.includes('financial/reports') ||
					pathname.includes('ifr') ||
					pathname.includes('plan')
				) {
					breadcrumbsProps.crumbs.push({
						children: t('ifrs'),
						to: `${urlProject}/reports/financial`,
					});
				}

				if (pathname.includes('reports/financial')) {
					breadcrumbsProps.page = t('ifrs');

					if (pathname.includes('financial/plans')) {
						breadcrumbsProps.page = t('plans');
					} else if (pathname.includes('financial/reports')) {
						breadcrumbsProps.page = t('reports');
					}
				} else if (pathname.includes('ifr') || pathname.includes('plan')) {
					if (pathname.includes('ifr')) {
						breadcrumbsProps.crumbs.push({
							children: t('reports'),
							to: `${urlProject}/reports/financial/reports`,
						});

						breadcrumbsProps.page = ifr?.name || t('report');
					}

					if (pathname.includes('plan')) {
						breadcrumbsProps.crumbs.push({
							children: t('plans'),
							to: `${urlProject}/reports/financial/plans`,
						});

						breadcrumbsProps.page = ifrPlan?.name || t('plan');
					}
				}

				if (pathname.includes('soe')) {
					breadcrumbsProps.page = t('soe');
				}
			}

			const projectHeader = (
				<Grid container spacing={2}>
					<Grid
						item
						xs={11}
						lg={12}
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
						}}>
						<Breadcrumbs {...breadcrumbsProps} />
					</Grid>
					<Grid item xs={12}>
						<Title
							actions={titleActions}
							ChipStatusProps={{
								status: project.status,
							}}
							color="purple"
							IconCircleProps={{
								type: 'project',
							}}
							id={id}
							overline={t('project')}
							title={project.name}
						/>
					</Grid>
					{project?.version !== '2' && (
						<>
							<Grid item xs={12}>
								<Tabs onChange={handleChangeTab} tabs={tabs} value={tabValue} />
							</Grid>
							<Grid item xs={12}>
								<Outlet />
							</Grid>
						</>
					)}
				</Grid>
			);

			return (
				<>
					<Box
						sx={
							project?.version === '2'
								? {
										bgcolor: 'background.paper',
										pt: 2,
										pb: 2,
										// No `px:` here — Breadcrumbs and Title both wrap in MUI
										// `Container` which already supplies horizontal padding.
										// Adding `px: 2` here doubles the left inset vs v1.
										'& nav.MuiBox-root': { pt: 1 },
									}
								: undefined
						}>
						{projectHeader}
					</Box>
					{project?.version === '2' && <ProjectSidebar project={project} />}
					{!isOnboarded && (
						<DialogOnboardProject
							isOpen={paramDialog === 'onboardProject'}
							onClose={handleCloseDialogOnboardProject}
							onSuccess={handleOnboardingSuccess}
							project={project as unknown as ProjectPayload}
						/>
					)}
				</>
			);
		}

		return (
			<Empty
				actions={emptyActions}
				content={{ children: t('empty.project.content') }}
				title={{ children: t('empty.project.title') }}
				IconCircleProps={{ type: 'project' }}
				variant="page"
			/>
		);
	};

	return (
		<ContextProject.Provider
			value={{
				isLoadingProject: isLoadingProjectOrWbgProject,
				isOnboarded,
				project,
				refetchProject,
				setProject,
			}}>
			<ContextActivities.Provider
				value={{
					activities,
					isLoadingActivities,
					paginationActivities,
					refetchActivities,
					setActivities,
					setPaginationActivities,
				}}>
				<ContextActs.Provider
					value={{
						acts,
						isLoadingActs,
						paginationActs,
						refetchActs,
						setActs,
						setPaginationActs,
					}}>
					<ContextLoans.Provider
						value={{ isLoadingLoans, loans, paginationLoans, refetchLoans, setLoans }}>
						<ContextEntitiesSummary.Provider
							value={{
								entitiesSummary,
								isLoadingEntitiesSummary,
								refetchEntitiesSummary,
								setEntitiesSummary,
							}}>
							<ContextEntities.Provider
								value={{
									entities,
									isLoadingEntities,
									paginationEntities,
									refetchEntities,
									setEntities,
									setPaginationEntities,
								}}>
								<ContextSuppliers.Provider
									value={{
										isLoadingSuppliers,
										paginationSuppliers,
										refetchSuppliers,
										setPaginationSuppliers,
										setSuppliers,
										suppliers,
									}}>
									<ContextBeneficiaries.Provider
										value={{
											isLoadingBeneficiaries,
											paginationBeneficiaries,
											refetchBeneficiaries,
											setPaginationBeneficiaries,
											setBeneficiaries,
											beneficiaries,
										}}>
										<ContextAccountsDesignated.Provider
											value={{
												accountsDesignated,
												isLoadingAccountsDesignated,
												paginationAccountsDesignated,
												refetchAccountsDesignated,
												setAccountsDesignated,
												setPaginationAccountsDesignated,
											}}>
											<ContextAccountsLocal.Provider
												value={{
													accountsLocal,
													isLoadingAccountsLocal,
													paginationAccountsLocal,
													refetchAccountsLocal,
													setAccountsLocal,
													setPaginationAccountsLocal,
												}}>
												<ContextContracts.Provider
													value={{
														contracts,
														isLoadingContracts,
														paginationContracts,
														refetchContracts,
														setContracts,
														setPaginationContracts,
													}}>
													<ContextReimbursements.Provider
														value={{
															isLoadingReimbursements,
															paginationReimbursements,
															refetchReimbursements,
															reimbursements,
															setPaginationReimbursements,
															setReimbursements,
														}}>
														<ContextAdvances.Provider
															value={{
																advances,
																isLoadingAdvances,
																paginationAdvances,
																refetchAdvances,
																setAdvances,
																setPaginationAdvances,
															}}>
															<ContextPaymentRefunds.Provider
																value={{
																	isLoadingPaymentRefunds,
																	paginationPaymentRefunds,
																	refetchPaymentRefunds,
																	paymentRefunds,
																	setPaginationPaymentRefunds,
																	setPaymentRefunds,
																}}>
																<ContextPayments.Provider
																	value={{
																		isLoadingPayments,
																		payments,
																		paginationPayments,
																		refetchPayments,
																		setPayments,
																		setPaginationPayments,
																	}}>
																	<ContextSpecialCommitments.Provider
																		value={{
																			isLoadingSpecialCommitments,
																			paginationSpecialCommitments,
																			refetchSpecialCommitments,
																			specialCommitments,
																			setPaginationSpecialCommitments,
																			setSpecialCommitments,
																		}}>
																		<ContextCashBenefits.Provider
																			value={{
																				cashBenefits,
																				isLoadingCashBenefits,
																				paginationCashBenefits,
																				refetchCashBenefits,
																				setCashBenefits,
																				setPaginationCashBenefits,
																			}}>
																			<ContextNonCashBenefits.Provider
																				value={{
																					nonCashBenefits,
																					isLoadingNonCashBenefits,
																					paginationNonCashBenefits,
																					refetchNonCashBenefits,
																					setNonCashBenefits,
																					setPaginationNonCashBenefits,
																				}}>
																				<ContextTransfers.Provider
																					value={{
																						isLoadingTransfers,
																						transfers,
																						paginationTransfers,
																						refetchTransfers,
																						setTransfers,
																						setPaginationTransfers,
																					}}>
																					<ContextOpEx.Provider
																						value={{
																							isLoadingOpEx,
																							opEx,
																							paginationOpEx,
																							refetchOpEx,
																							setOpEx,
																							setPaginationOpEx,
																						}}>
																						<ContextHistoricalTransactions.Provider
																							value={{
																								historicalTransactions,
																								isLoadingHistoricalTransactions,
																								paginationHistoricalTransactions,
																								refetchHistoricalTransactions,
																								setHistoricalTransactions,
																								setPaginationHistoricalTransactions,
																							}}>
																							<ContextDocumentsBeneficiaries.Provider
																								value={{
																									documentsBeneficiaries,
																									isLoadingDocumentsBeneficiaries,
																									paginationDocumentsBeneficiaries,
																									refetchDocumentsBeneficiaries,
																									setDocumentsBeneficiaries,
																									setPaginationDocumentsBeneficiaries,
																								}}>
																								<ContextDocumentsSuppliers.Provider
																									value={{
																										documentsSuppliers,
																										isLoadingDocumentsSuppliers,
																										paginationDocumentsSuppliers,
																										refetchDocumentsSuppliers,
																										setDocumentsSuppliers,
																										setPaginationDocumentsSuppliers,
																									}}>
																									<ContextUserProjectPermissions.Provider
																										value={{ userProjectPermissions }}>
																										<ContextUsersPermissions.Provider
																											value={{
																												isLoadingUsersPermissions,
																												paginationUsersPermissions,
																												refetchUsersPermissions,
																												setUsersPermissions,
																												setPaginationUsersPermissions,
																												usersPermissions,
																											}}>
																											<ContextIfr.Provider
																												value={{
																													ifr,
																													ifrPayload,
																													ifrTables,
																													isLoadingIfr,
																													refetchIfr,
																													setIfr,
																													setIfrPayload,
																													setIfrTables,
																												}}>
																												<ContextIfrs.Provider
																													value={{
																														ifrs,
																														isLoadingIfrs,
																														paginationIfrs,
																														refetchIfrs,
																														setIfrs,
																														setPaginationIfrs,
																													}}>
																													<ContextIfrPlans.Provider
																														value={{
																															ifrPlans,
																															isLoadingIfrPlans,
																															paginationIfrPlans,
																															refetchIfrPlans,
																															setIfrPlans,
																															setPaginationIfrPlans,
																														}}>
																														<ContextIfrPlan.Provider
																															value={{
																																ifrPlan,
																																isLoadingIfrPlan,
																																refetchIfrPlan,
																																setIfrPlan,
																															}}>
																															<ContextIfrPlanVersion.Provider
																																value={{
																																	ifrPlanVersion,
																																	ifrPlanVersionPayload,
																																	setIfrPlanVersion,
																																	setIfrPlanVersionPayload,
																																}}>
																																<Authenticated>
																																	{user && featureFlags['CRM'] && (
																																		<BtnFab
																																			color="primary"
																																			icon={<IconQuestionMark />}
																																			title={t('submitFeedback')}
																																			sx={(theme) => {
																																				return {
																																					position: 'fixed',
																																					bottom: theme.spacing(3),
																																					right: theme.spacing(3),
																																				};
																																			}}
																																			onClick={() => {
																																				searchParams.set(
																																					'dialog',
																																					'addIncident'
																																				);
																																				setSearchParams(
																																					searchParams
																																				);
																																			}}
																																		/>
																																	)}
																																	<DialogAddIncident
																																		isOpen={
																																			paramDialog === 'addIncident'
																																		}
																																		onClose={() => {
																																			setSearchParams((params) => {
																																				params.delete('dialog');
																																				params.delete(
																																					'addIncident'
																																				);
																																				return params;
																																			});
																																		}}
																																	/>
																																	<Box
																																		sx={{ position: 'relative' }}>
																																		{renderTemplate()}
																																	</Box>
																																</Authenticated>
																															</ContextIfrPlanVersion.Provider>
																														</ContextIfrPlan.Provider>
																													</ContextIfrPlans.Provider>
																												</ContextIfrs.Provider>
																											</ContextIfr.Provider>
																										</ContextUsersPermissions.Provider>
																									</ContextUserProjectPermissions.Provider>
																								</ContextDocumentsSuppliers.Provider>
																							</ContextDocumentsBeneficiaries.Provider>
																						</ContextHistoricalTransactions.Provider>
																					</ContextOpEx.Provider>
																				</ContextTransfers.Provider>
																			</ContextNonCashBenefits.Provider>
																		</ContextCashBenefits.Provider>
																	</ContextSpecialCommitments.Provider>
																</ContextPayments.Provider>
															</ContextPaymentRefunds.Provider>
														</ContextAdvances.Provider>
													</ContextReimbursements.Provider>
												</ContextContracts.Provider>
											</ContextAccountsLocal.Provider>
										</ContextAccountsDesignated.Provider>
									</ContextBeneficiaries.Provider>
								</ContextSuppliers.Provider>
							</ContextEntities.Provider>
						</ContextEntitiesSummary.Provider>
					</ContextLoans.Provider>
				</ContextActs.Provider>
			</ContextActivities.Provider>
		</ContextProject.Provider>
	);
};
